import React, { useState, useRef, useEffect, useCallback } from 'react';
import { PersonSummaryWithPhoto } from '../types';
import { 
  optimizeImageUrl, 
  getImageSizeForContext, 
  globalImageCache, 
  globalPerformanceMonitor,
  OptimizedIntersectionObserver
} from '../utils/imageOptimization';
import { getConfig } from '../config';

interface PersonPhotoProps {
  person: PersonSummaryWithPhoto;
  size?: 'small' | 'medium' | 'large';
  onClick?: (person: PersonSummaryWithPhoto) => void;
  showTooltip?: boolean;
  className?: string;
  forceVisible?: boolean; // For testing purposes
  context?: 'overlay' | 'grid' | 'detail' | 'thumbnail';
  enableOptimizations?: boolean;
}

const PersonPhoto: React.FC<PersonPhotoProps> = ({
  person,
  size = 'medium',
  onClick,
  showTooltip = true,
  className = '',
  forceVisible = false,
  context = 'overlay',
  enableOptimizations = true
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltipState, setShowTooltipState] = useState(false);
  const [optimizedImageSrc, setOptimizedImageSrc] = useState<string>('');
  
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<OptimizedIntersectionObserver | null>(null);

  const config = getConfig();

  // Size configurations with performance optimizations
  const sizeConfig = {
    small: {
      container: 'w-10 h-10',
      image: 'w-10 h-10',
      text: 'text-xs'
    },
    medium: {
      container: 'w-12 h-12',
      image: 'w-12 h-12', 
      text: 'text-sm'
    },
    large: {
      container: 'w-16 h-16',
      image: 'w-16 h-16',
      text: 'text-base'
    }
  };

  const styleConfig = sizeConfig[size];

  // Get optimized image configuration
  const imageConfig = getImageSizeForContext(context, size);

  // Optimized image loading with caching
  const loadOptimizedImage = useCallback(() => {
    if (!person.imageUrl) return;

    const optimizedUrl = enableOptimizations 
      ? optimizeImageUrl(person.imageUrl, imageConfig, config.imagesCdnUrl)
      : person.imageUrl;

    // Check cache first
    const cachedImage = globalImageCache.get(optimizedUrl);
    if (cachedImage) {
      globalPerformanceMonitor.startLoad(optimizedUrl, imageConfig, true);
      globalPerformanceMonitor.endLoad(optimizedUrl, true);
      setOptimizedImageSrc(optimizedUrl);
      setImageLoaded(true);
      setImageError(false);
      return;
    }

    // Start performance monitoring
    globalPerformanceMonitor.startLoad(optimizedUrl, imageConfig, false);

    // Create new image for loading
    const img = new Image();
    
    img.onload = () => {
      globalImageCache.set(optimizedUrl, img);
      globalPerformanceMonitor.endLoad(optimizedUrl, true);
      setOptimizedImageSrc(optimizedUrl);
      setImageLoaded(true);
      setImageError(false);
    };

    img.onerror = () => {
      const error = `Failed to load optimized image: ${optimizedUrl}`;
      globalPerformanceMonitor.endLoad(optimizedUrl, false, error);
      setImageError(true);
      setImageLoaded(false);
    };

    // Set crossOrigin for CDN images
    if (enableOptimizations && config.imagesCdnUrl) {
      img.crossOrigin = 'anonymous';
    }

    img.src = optimizedUrl;
  }, [person.imageUrl, imageConfig, enableOptimizations, config.imagesCdnUrl]);

  // Enhanced Intersection Observer for lazy loading
  useEffect(() => {
    // If forceVisible is true (for testing), immediately set as visible
    if (forceVisible) {
      setIsVisible(true);
      return;
    }

    // Use optimized intersection observer if optimizations are enabled
    if (enableOptimizations && config.enableLazyLoading) {
      observerRef.current = new OptimizedIntersectionObserver({
        threshold: 0.1,
        rootMargin: '50px',
        enableIntersectionObserver: true,
      });

      if (containerRef.current) {
        observerRef.current.observe(containerRef.current, () => {
          setIsVisible(true);
        });
      }

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    } else {
      // Fallback to standard intersection observer
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => observer.disconnect();
    }
  }, [forceVisible, enableOptimizations, config.enableLazyLoading]);

  // Load image when visible
  useEffect(() => {
    if (isVisible) {
      loadOptimizedImage();
    }
  }, [isVisible, loadOptimizedImage]);

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(person);
    }
  }, [onClick, person]);

  const handleMouseEnter = useCallback(() => {
    if (showTooltip) {
      setShowTooltipState(true);
    }
  }, [showTooltip]);

  const handleMouseLeave = useCallback(() => {
    setShowTooltipState(false);
  }, []);

  // Determine image source with fallback handling
  const getImageSrc = useCallback(() => {
    if (imageError) {
      return '/images/person-error.svg';
    }
    if (!person.imageUrl) {
      return '/images/person-placeholder.svg';
    }
    return optimizedImageSrc || person.imageUrl;
  }, [imageError, person.imageUrl, optimizedImageSrc]);

  // Handle direct image error events (for testing compatibility)
  const handleDirectImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(false);
  }, []);

  // Determine if image should be loaded
  const shouldLoadImage = isVisible;

  // Determine if this person photo is clickable
  const isClickable = !!onClick;

  // Preload placeholder and error images for better performance
  useEffect(() => {
    if (enableOptimizations) {
      const placeholderImg = new Image();
      placeholderImg.src = '/images/person-placeholder.svg';
      
      const errorImg = new Image();
      errorImg.src = '/images/person-error.svg';
    }
  }, [enableOptimizations]);

  return (
    <div
      ref={containerRef}
      className={`relative ${styleConfig.container} ${isClickable ? 'cursor-pointer group' : ''} ${className}`}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Image container with loading state */}
      <div className={`${styleConfig.container} rounded-full overflow-hidden bg-gray-700 flex items-center justify-center`}>
        {shouldLoadImage ? (
          <img
            ref={imgRef}
            src={getImageSrc()}
            alt={person.name}
            className={`${styleConfig.image} rounded-full object-cover transition-opacity duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
            onError={handleDirectImageError}
            // Add performance hints for optimized images
            {...(enableOptimizations && {
              decoding: 'async',
              fetchpriority: context === 'detail' ? 'high' : 'low',
            })}
          />
        ) : (
          // Placeholder while not visible or loading
          <div className={`${styleConfig.container} rounded-full bg-gray-600 flex items-center justify-center`}>
            <svg
              className={`${size === 'small' ? 'w-5 h-5' : size === 'medium' ? 'w-6 h-6' : 'w-8 h-8'} text-gray-400`}
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}

        {/* Loading spinner with performance optimization */}
        {shouldLoadImage && !imageLoaded && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-700 rounded-full">
            <div 
              className={`animate-spin rounded-full border-2 border-gray-300 border-t-white ${
                size === 'small' ? 'w-4 h-4' : size === 'medium' ? 'w-5 h-5' : 'w-6 h-6'
              }`}
              style={{ 
                // Optimize animation performance
                willChange: 'transform',
                transform: 'translateZ(0)',
              }}
            ></div>
          </div>
        )}
      </div>

      {/* Tooltip with performance optimization */}
      {showTooltip && showTooltipState && (
        <div 
          className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-50"
          style={{ 
            // Optimize tooltip rendering
            willChange: 'opacity, transform',
            transform: 'translateZ(0) translateX(-50%)',
          }}
        >
          <div className="bg-black/90 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap max-w-48">
            <div className={`font-semibold ${styleConfig.text}`}>{person.name}</div>
            {person.role && (
              <div className={`text-gray-300 ${size === 'small' ? 'text-xs' : 'text-sm'}`}>
                {person.role}
              </div>
            )}
            {person.character && (
              <div className={`text-gray-400 ${size === 'small' ? 'text-xs' : 'text-sm'}`}>
                as {person.character}
              </div>
            )}
            {/* Performance info in debug mode */}
            {config.enableDebugMode && enableOptimizations && (
              <div className="text-xs text-gray-500 mt-1 border-t border-gray-600 pt-1">
                {imageLoaded ? '✓ Optimized' : '⏳ Loading'}
              </div>
            )}
            {/* Tooltip arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-black/90"></div>
          </div>
        </div>
      )}

      {/* Click indicator and visual cues with performance optimization */}
      {isClickable && (
        <>
          {/* Hover border effect */}
          <div 
            className="absolute inset-0 rounded-full border-2 border-transparent group-hover:border-white/50 transition-colors duration-200"
            style={{ willChange: 'border-color' }}
          ></div>
          
          {/* Subtle overlay to indicate clickability */}
          <div 
            className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/10 transition-colors duration-200"
            style={{ willChange: 'background-color' }}
          ></div>
          
          {/* Click icon indicator (appears on hover) */}
          <div 
            className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style={{ willChange: 'opacity' }}
          >
            <div className="bg-black/70 rounded-full p-1">
              <svg 
                className={`${size === 'small' ? 'w-3 h-3' : size === 'medium' ? 'w-4 h-4' : 'w-5 h-5'} text-white`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M13 7l5 5m0 0l-5 5m5-5H6" 
                />
              </svg>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PersonPhoto;